import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Button, Form, Alert, InputGroup, Card, Badge, Spinner } from 'react-bootstrap';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import SiteHeader from './SiteHeader';
import { useLanguage } from '../contexts/LanguageContext';
import './Landing.css';
import { t } from '../utils/i18n';
import SaleProductCard from './SaleProductCard';
import './UserAuth.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

const GOOGLE_ICON_FALLBACK =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGcgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj48cGF0aCBkPSJNMTcuNjQgOS4yMDVjMC0uNjM5LS4wNTctMS4yNzItLjE2NC0xLjg2OUg5djMuNTI3aDQuODQ0Yy0uMjA5IDEuMTI1LS44NDQgMi4wNzctMS43OTYgMi44NDV2Mi4yNTVoMi45MDhjMS42OTgtMS42OTggMi42MTctNC4yMTEgMi42MTctNy4xODh6IiBmaWxsPSIjNDI4NUY0Ii8+PHBhdGggZD0iTTkgMThjMi40MyAwIDQuNDY3LS44MDYgNS45NjEtMi4xNjNsLTIuOTA4LTIuMjU1Yy0uODA1LjUzOS0xLjgzNi44NTgtMy4wNTMuODU4LTIuMzQ5IDAtNC4zMzctMS41ODQtNS4wNDUtMy43MTFIMC45NTR2Mi4zMzJDMi40MzggMTUuOTgzIDUuNDgyIDE4IDkgMTh6IiBmaWxsPSIjMzRBODUzIi8+PHBhdGggZD0iTTMuOTU1IDEwLjg3MUwuOTU0IDguNTM5Qy4zNDggOS44OTkgMCAxMS40MzQgMCAxM2MwIDEuNTY2LjM0OCAzLjEwMS45NTQgNC40NjFsMy4wMDEtMi4zMzJjLS40MDgtMS4xMjctLjQwOC0yLjM4NSAwLTMuNTEyeiIgZmlsbD0iI0ZCQkMwNCIvPjxwYXRoIGQ9Ik05IDMuNTU4YzEuMzIxIDAgMi41MDcuNDQyIDMuNDQwIDEuMzEyTDguNDQxIDcuMDQ1QzcuNzYzIDYuNzE2IDYuODQxIDYuNDQ0IDUuODI4IDYuNDQ0IDMuNDc5IDYuNDQ0IDEuNDY3IDguMDI4Ljc1OSAxMC4xNjRsLTMuMDAxLTIuMzMyQzIuNDM4IDQuOTk3IDUuNDgyIDMuNTU4IDkgMy41NTh6IiBmaWxsPSIjRUE0MzM1Ii8+PC9nPjwvc3ZnPg==';

function GoogleIcon() {
  return (
    <img
      src="/google_login.png"
      alt=""
      className="user-auth-google-icon"
      onError={(e) => {
        e.target.src = GOOGLE_ICON_FALLBACK;
      }}
    />
  );
}

function UserAuth() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [authMode, setAuthMode] = useState('login');
  const [oauthError, setOauthError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [purchases, setPurchases] = useState([]);
  const [offers, setOffers] = useState([]);
  const [saleProducts, setSaleProducts] = useState([]);

  const servicesBase = language === 'en' ? '/services' : '/servicios';

  const getProductHref = (slug) => `${servicesBase}/${slug}`;
  const getCheckoutHref = (slug) => `/checkout/product?slug=${encodeURIComponent(slug)}`;

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const [regEmail, setRegEmail] = useState('');
  const [regName, setRegName] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirm, setRegConfirm] = useState('');
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showRegConfirm, setShowRegConfirm] = useState(false);
  const [regLoading, setRegLoading] = useState(false);
  const [regError, setRegError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const loadMe = async () => {
      try {
        const me = await fetch(`${API_BASE}/auth/me`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : null));
        if (!me || cancelled) return;
        setCurrentUser(me);
        const [purchasesRes, offersRes] = await Promise.all([
          fetch(`${API_BASE}/api/client/purchases`, { credentials: 'include' }),
          fetch(`${API_BASE}/api/client/offers`, { credentials: 'include' })
        ]);
        const p = purchasesRes.ok ? await purchasesRes.json() : { purchases: [] };
        const o = offersRes.ok ? await offersRes.json() : { offers: [] };
        if (!cancelled) {
          setPurchases(Array.isArray(p.purchases) ? p.purchases : []);
          setOffers(Array.isArray(o.offers) ? o.offers : []);
          setSaleProducts(Array.isArray(o.saleProducts) ? o.saleProducts : []);
        }
      } finally {
        if (!cancelled) setDashboardLoading(false);
      }
    };
    loadMe();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get('error');
    if (!errorParam) return;
    const errorMessages = {
      google_not_configured: t('landing.errorGoogleNotConfigured', language),
      google_auth_failed: t('landing.errorGoogleAuthFailed', language),
      no_user: t('landing.errorNoUser', language),
      callback_error: t('landing.errorCallback', language),
      session_error: t('landing.errorSession', language)
    };
    setOauthError(errorMessages[errorParam] || t('landing.errorSignIn', language));
    params.delete('error');
    const qs = params.toString();
    window.history.replaceState({}, '', qs ? `/user?${qs}` : '/user');
  }, [language]);

  const redirectAfterAuth = async () => {
    const me = await fetch(`${API_BASE}/auth/me`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : null));
    if (!me) return;
    setCurrentUser(me);
    const returnTo = searchParams.get('return');
    if (
      returnTo &&
      returnTo.startsWith('/') &&
      !returnTo.startsWith('//') &&
      !returnTo.startsWith('/app/dashboard')
    ) {
      navigate(returnTo, { replace: true });
      return;
    }
    if (['admin', 'manager', 'sales'].includes(me.role)) {
      navigate('/app/dashboard', { replace: true });
    } else {
      const [purchasesRes, offersRes] = await Promise.all([
        fetch(`${API_BASE}/api/client/purchases`, { credentials: 'include' }),
        fetch(`${API_BASE}/api/client/offers`, { credentials: 'include' })
      ]);
      const p = purchasesRes.ok ? await purchasesRes.json() : { purchases: [] };
      const o = offersRes.ok ? await offersRes.json() : { offers: [] };
      setPurchases(Array.isArray(p.purchases) ? p.purchases : []);
      setOffers(Array.isArray(o.offers) ? o.offers : []);
      setSaleProducts(Array.isArray(o.saleProducts) ? o.saleProducts : []);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setOauthError('');
    setLoginLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword }),
        credentials: 'include'
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setLoginError(data.error || t('userAuth.errorSignInGeneric', language));
        setLoginLoading(false);
        return;
      }
      await redirectAfterAuth();
    } catch {
      setLoginError(t('landing.errorConnection', language));
      setLoginLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegError('');
    setOauthError('');
    if (regPassword !== regConfirm) {
      setRegError(t('userAuth.errorPasswordMismatch', language));
      return;
    }
    if (regPassword.length < 6) {
      setRegError(t('userAuth.errorPasswordShort', language));
      return;
    }
    setRegLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: regEmail,
          password: regPassword,
          name: regName.trim() || regEmail.split('@')[0]
        }),
        credentials: 'include'
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setRegError(data.error || t('userAuth.errorRegisterGeneric', language));
        setRegLoading(false);
        return;
      }
      await redirectAfterAuth();
    } catch {
      setRegError(t('landing.errorConnection', language));
      setRegLoading(false);
    }
  };

  const goGoogle = () => {
    window.location.href = `${API_BASE}/auth/google`;
  };

  const handleLogout = async () => {
    await fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
    setCurrentUser(null);
    setPurchases([]);
    setOffers([]);
  };

  const pwToggle = (visible, setVisible) => (
    <Button
      type="button"
      variant="outline-secondary"
      className="user-auth-toggle-pw"
      onClick={() => setVisible((v) => !v)}
      aria-label={visible ? t('userAuth.hidePassword', language) : t('userAuth.showPassword', language)}
    >
      {visible ? <FaEyeSlash /> : <FaEye />}
    </Button>
  );

  const switchMode = (mode) => {
    setAuthMode(mode);
    setLoginError('');
    setRegError('');
    setOauthError('');
  };

  if (dashboardLoading) {
    return (
      <div className="landing landing-theme-dark min-vh-100">
        <SiteHeader variant="app" />
        <div className="user-auth-page site-main-with-header">
          <div className="user-auth-background" aria-hidden />
          <Container className="user-auth-container text-center">
            <Spinner animation="border" variant="light" />
          </Container>
        </div>
      </div>
    );
  }

  if (currentUser) {
    const isStaff = ['admin', 'manager', 'sales'].includes(currentUser.role);
    return (
      <div className="landing landing-theme-dark min-vh-100">
        <SiteHeader variant="app" />
        <div className="user-auth-page site-main-with-header">
        <div className="user-auth-background" aria-hidden />
        <Container className="user-auth-container">
          <div className="text-center mb-4">
            <Link to="/" className="user-auth-back-link">
              {t('common.blog.backToHome', language)}
            </Link>
            <h1 className="user-auth-page-title mt-3 mb-0">{t('userAuth.dashboardTitle', language)}</h1>
            <p className="user-auth-page-subtitle mt-2 mb-0">{t('userAuth.dashboardSubtitle', language)}</p>
          </div>
          <Row className="g-4">
            <Col lg={6}>
              <Card className="user-auth-card">
                <h2 className="user-auth-card-title">{t('userAuth.purchasesTitle', language)}</h2>
                {purchases.length === 0 ? (
                  <p className="text-muted mb-0">{t('userAuth.noPurchases', language)}</p>
                ) : purchases.map((purchase) => (
                  <div key={purchase.id} className="mb-3 pb-3 border-bottom">
                    <div className="d-flex justify-content-between align-items-center">
                      <strong>#{purchase.id}</strong>
                      <Badge bg="secondary">{purchase.status}</Badge>
                    </div>
                    <div className="small text-muted">
                      {purchase.currency} {purchase.total}
                    </div>
                  </div>
                ))}
              </Card>
            </Col>
            <Col lg={6}>
              <Card className="user-auth-card">
                <h2 className="user-auth-card-title">{t('userAuth.offersTitle', language)}</h2>
                {saleProducts.length > 0 && (
                  <>
                    <p className="user-auth-offers-lead small text-muted mb-3">
                      {t('userAuth.offersDiscountHint', language)}
                    </p>
                    <div className="user-auth-sale-grid mb-3">
                      {saleProducts.map((product) => (
                        <SaleProductCard
                          key={product.id}
                          product={product}
                          language={language}
                          productHref={getProductHref(product.slug)}
                          checkoutHref={getCheckoutHref(product.slug)}
                          showPayNow={!!product.direct_checkout}
                        />
                      ))}
                    </div>
                  </>
                )}
                {offers.length > 0 && (
                  <>
                    {saleProducts.length > 0 && (
                      <h3 className="user-auth-offers-subtitle h6 mb-2">
                        {t('userAuth.personalCouponsTitle', language)}
                      </h3>
                    )}
                    {offers.map((offer) => (
                      <div key={offer.id} className="mb-3 pb-3 border-bottom user-auth-coupon-row">
                        <strong>{offer.title}</strong>
                        <p className="small text-muted mb-1">{offer.description || ''}</p>
                        {offer.coupon_code && <Badge bg="success">{offer.coupon_code}</Badge>}
                      </div>
                    ))}
                  </>
                )}
                {saleProducts.length === 0 && offers.length === 0 && (
                  <p className="text-muted mb-0">{t('userAuth.noOffers', language)}</p>
                )}
              </Card>
            </Col>
          </Row>
          <div className="d-flex justify-content-center gap-2 mt-4 flex-wrap">
            {isStaff && (
              <Link to="/app/dashboard" className="landing-btn-primary">
                {t('userAuth.goToAdmin', language)}
              </Link>
            )}
            <Button type="button" className="user-auth-btn-logout" variant="outline-secondary" onClick={handleLogout}>
              {t('userAuth.logout', language)}
            </Button>
          </div>
        </Container>
        </div>
      </div>
    );
  }

  return (
    <div className="landing landing-theme-dark min-vh-100">
      <SiteHeader variant="app" />
      <div className="user-auth-page site-main-with-header">
      <div className="user-auth-background" aria-hidden />

      <Container className="user-auth-container">
        <div className="text-center mb-4">
          <Link to="/" className="user-auth-back-link">
            {t('common.blog.backToHome', language)}
          </Link>
          <h1 className="user-auth-page-title mt-3 mb-0">{t('userAuth.pageTitle', language)}</h1>
          <p className="user-auth-page-subtitle mt-2 mb-0">{t('userAuth.pageSubtitle', language)}</p>
        </div>

        {oauthError && (
          <Alert
            variant="danger"
            className="mb-4 mx-auto"
            style={{ maxWidth: 920 }}
            dismissible
            onClose={() => setOauthError('')}
          >
            {oauthError}
          </Alert>
        )}

        <Row className="justify-content-center">
          <Col lg={6} xl={5}>
            <div className="user-auth-card">
              <h2 className="user-auth-card-title">
                {authMode === 'login' ? t('userAuth.loginTitle', language) : t('userAuth.signupTitle', language)}
              </h2>

              {authMode === 'login' ? (
                <Form onSubmit={handleLogin}>
                  {loginError && (
                    <Alert variant="danger" className="py-2 small">
                      {loginError}
                    </Alert>
                  )}
                  <Form.Group className="mb-3">
                    <Form.Label className="small">{t('userAuth.email', language)}</Form.Label>
                    <Form.Control
                      type="email"
                      autoComplete="email"
                      className="landing-domain-input"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      placeholder={t('userAuth.emailPlaceholder', language)}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="small">{t('userAuth.password', language)}</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showLoginPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        className="landing-domain-input"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        placeholder={t('userAuth.passwordPlaceholder', language)}
                        required
                      />
                      {pwToggle(showLoginPassword, setShowLoginPassword)}
                    </InputGroup>
                  </Form.Group>
                  <Button type="submit" className="landing-btn-primary w-100 mb-3 border-0" disabled={loginLoading}>
                    {loginLoading ? t('common.loading', language) : t('userAuth.loginSubmit', language)}
                  </Button>
                </Form>
              ) : (
                <Form onSubmit={handleRegister}>
                  {regError && (
                    <Alert variant="danger" className="py-2 small">
                      {regError}
                    </Alert>
                  )}
                  <Form.Group className="mb-3">
                    <Form.Label className="small">{t('userAuth.email', language)}</Form.Label>
                    <Form.Control
                      type="email"
                      autoComplete="email"
                      className="landing-domain-input"
                      value={regEmail}
                      onChange={(e) => setRegEmail(e.target.value)}
                      placeholder={t('userAuth.emailPlaceholder', language)}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="small">{t('userAuth.nameOptional', language)}</Form.Label>
                    <Form.Control
                      type="text"
                      autoComplete="name"
                      className="landing-domain-input"
                      value={regName}
                      onChange={(e) => setRegName(e.target.value)}
                      placeholder={t('userAuth.namePlaceholder', language)}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="small">{t('userAuth.password', language)}</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showRegPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        className="landing-domain-input"
                        value={regPassword}
                        onChange={(e) => setRegPassword(e.target.value)}
                        placeholder={t('userAuth.passwordCreatePlaceholder', language)}
                        required
                        minLength={6}
                      />
                      {pwToggle(showRegPassword, setShowRegPassword)}
                    </InputGroup>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="small">{t('userAuth.confirmPassword', language)}</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showRegConfirm ? 'text' : 'password'}
                        autoComplete="new-password"
                        className="landing-domain-input"
                        value={regConfirm}
                        onChange={(e) => setRegConfirm(e.target.value)}
                        placeholder={t('userAuth.confirmPasswordPlaceholder', language)}
                        required
                        minLength={6}
                      />
                      {pwToggle(showRegConfirm, setShowRegConfirm)}
                    </InputGroup>
                  </Form.Group>
                  <Button type="submit" className="landing-btn-primary w-100 mb-3 border-0" disabled={regLoading}>
                    {regLoading ? t('common.loading', language) : t('userAuth.signupSubmit', language)}
                  </Button>
                </Form>
              )}

              <p className="text-center small mb-0">
                <button
                  type="button"
                  className="user-auth-inline-link border-0 bg-transparent p-0"
                  onClick={() => switchMode(authMode === 'login' ? 'signup' : 'login')}
                >
                  {authMode === 'login' ? t('userAuth.noAccountSignup', language) : t('userAuth.haveAccountLogin', language)}
                </button>
              </p>
              <div className="user-auth-or">
                <span>{t('userAuth.or', language)}</span>
              </div>
              <Button type="button" variant="outline-secondary" className="user-auth-btn-google" onClick={goGoogle}>
                <GoogleIcon /> {t('userAuth.continueWithGoogle', language)}
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
      </div>
    </div>
  );
}

export default UserAuth;
